import { useCallback, useEffect, useRef } from "react";
import type { RealtimeEventPayload } from "@/lib/realtimeClient";

const ORDER_SCOPE = /^(?:auftraege|project|projects):(\d+)$/;

function addId(ids: Set<number>, raw: unknown): boolean {
  const id = Number(raw);
  if (!Number.isFinite(id) || id <= 0) return false;
  ids.add(id);
  return true;
}

/**
 * Liefert die Auftrags-IDs, die das Event ausdrücklich nennt.
 * `null` = kein konkretes Projekt (z. B. Schema-Update oder Polling).
 */
export function realtimeEventOrderIds(payload: RealtimeEventPayload): number[] | null {
  const ids = new Set<number>();
  let specific = false;

  for (const scope of payload.scopes || []) {
    const match = ORDER_SCOPE.exec(String(scope || "").trim().toLowerCase());
    if (!match) continue;
    specific = true;
    addId(ids, match[1]);
  }

  const data = payload.data;
  if (data && typeof data === "object") {
    if (addId(ids, data.auftrag_id ?? data.order_id)) specific = true;
    const many = data.auftrag_ids;
    if (Array.isArray(many)) {
      for (const raw of many) {
        if (addId(ids, raw)) specific = true;
      }
    }
  }

  if (!specific) return null;
  return [...ids];
}

/** Ob ein Realtime-Event diese Projektübersicht neu laden soll. */
export function realtimeEventConcernsOrder(
  payload: RealtimeEventPayload | undefined,
  orderId: number | null,
): boolean {
  if (!payload || orderId == null) return true;
  const ids = realtimeEventOrderIds(payload);
  if (ids == null) return true;
  return ids.includes(orderId);
}

/** Mehrere Events kurz hintereinander zu einem Aufruf bündeln. */
export function useTrailingCallback(fn: () => void, ms: number): () => void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current != null) window.clearTimeout(timerRef.current);
    };
  }, []);

  return useCallback(() => {
    if (timerRef.current != null) window.clearTimeout(timerRef.current);
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      fnRef.current();
    }, ms);
  }, [ms]);
}
